import React, { useState } from 'react';
import { useGetEventTypesQuery } from '../../event_manager/eventManagerApiSlice';
import { useGetAwardOptionsQuery } from '../../calendar/eventAdminCalendarSlice';
import {
  CalendarFilled,
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  CrownOutlined,
  DollarCircleFilled,
  FileAddFilled,
  GiftFilled,
  IdcardFilled,
  LockFilled,
  NotificationFilled,
  SmileOutlined,
  StarFilled,
  TeamOutlined,
  TrophyFilled,
  UserAddOutlined,
  UsergroupAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import SvgOnlineBookings from '../../../../../admin/assets/images/Dashboard/OnlineBookings';
import SvgAdminBookings from '../../../../../admin/assets/images/Dashboard/AdminBookings';
import SvgCancellations from '../../../../../admin/assets/images/Dashboard/Cancellations';
import { costString } from '../../../../../../utils/cost';
import StatsCard from './StatsCard';
import { Tabs } from 'antd';
import { useGetEventTypesForRDOQuery } from '../../../../rdo_compliance_management/complianceManagerSlice';
function StatisticsTabs({ statsData, loading = false, isSuperAdmin = false }) {
  const [activeOuterKey, setActiveOuterKey] = useState('events');
  const [activeInnerKey, setActiveInnerKey] = useState('all');

  const { data: adminEventTypes } = useGetEventTypesQuery(undefined, {
    skip: isSuperAdmin,
  });
  const { data: superAdminEventTypes } = useGetEventTypesForRDOQuery(
    undefined,
    { skip: !isSuperAdmin }
  );
  const eventTypes = adminEventTypes ?? superAdminEventTypes;
  const { data: awardTypes } = useGetAwardOptionsQuery();

  const nestedCategories = ['events', 'bookings', 'participants', 'revenue'];
  const statsMapping = {
    events: [
      {
        title: 'Total Events Created',
        key: 'total_events',
        icon: <CalendarFilled />,
      },
      {
        title: 'Paid Events Created',
        key: 'paid_events',
        icon: <DollarCircleFilled />,
      },
      {
        title: 'Free Events Created',
        key: 'free_events',
        icon: <GiftFilled />,
      },
      {
        title: 'Private Events Created',
        key: 'private_events',
        icon: <LockFilled />,
      },
      {
        title: 'Public Events Created',
        key: 'public_events',
        icon: <NotificationFilled />,
      },
      {
        title: 'RLWs Requested',
        key: 'rlws_requested',
        icon: <FileAddFilled />,
      },
      {
        title: 'RLWs Approved',
        key: 'rlws_approved',
        icon: <CheckCircleFilled />,
      },
      {
        title: 'RLWs Rejected',
        key: 'rlws_rejected',
        icon: <CloseCircleFilled />,
      },
    ],
    bookings: [
      {
        title: 'Total Bookings',
        key: 'total_bookings',
        icon: <CalendarFilled />,
      },
      {
        title: 'Online Bookings',
        key: 'online_bookings',
        icon: <SvgOnlineBookings />,
      },
      {
        title: 'Admin Bookings',
        key: 'admin_bookings',
        icon: <SvgAdminBookings />,
      },
      {
        title: 'Cancellations',
        key: 'cancelled_bookings',
        icon: <SvgCancellations />,
        invertColours: true,
      },
      {
        title: 'Free Bookings',
        key: 'free_bookings',
        icon: <GiftFilled />,
      },
      {
        title: 'Paid Bookings',
        key: 'paid_bookings',
        icon: <DollarCircleFilled />,
      },
      {
        title: 'Unique Users',
        key: 'unique_users_booked',
        icon: <UserOutlined />,
      },
      {
        title: 'Avg. Tickets Per Booking',
        key: 'average_tickets_per_booking',
        icon: <ClockCircleFilled />,
        valueMap: (v) => Math.round(v * 100) / 100.0,
      },
    ],
    participants: [
      {
        title: 'Total Tickets Booked',
        key: 'total_anglers',
        icon: <TeamOutlined />,
      },
      {
        title: 'Total Unique Anglers',
        key: 'total_unique_anglers',
        icon: <TeamOutlined />,
      },
      {
        title: 'Adult Tickets',
        key: 'adult_anglers',
        icon: <UsergroupAddOutlined />,
      },
      {
        title: 'Junior Tickets',
        key: 'under_18_anglers',
        icon: <SmileOutlined />,
      },
      {
        title: 'Checked-in Tickets',
        key: 'checked_in_anglers',
        icon: <CheckCircleFilled />,
      },
    ],
    revenue: [
      {
        title: 'Online Revenue',
        key: 'online_payments_pence',
        icon: <SvgOnlineBookings />,
        valueMap: (v) => costString(v / 100.0),
      },
      {
        title: 'Admin Revenue',
        key: 'in_person_payments_pence',
        icon: <SvgAdminBookings />,
        valueMap: (v) => costString(v / 100.0),
      },
      {
        title: 'Total Revenue',
        key: 'total_amount_pence',
        icon: <DollarCircleFilled />,
        valueMap: (v) => costString(v / 100.0),
      },
    ],
    awards: [
      {
        title: 'Awards Granted',
        key: 'total_awards_granted',
        icon: <TrophyFilled />,
      },
      {
        title: 'Unique Anglers Awarded',
        key: 'unique_awardees',
        icon: <TeamOutlined />,
      },
      {
        title: 'Physical Certificates Requested',
        key: 'certificates_requested',
        icon: <IdcardFilled />,
      },
    ],
    platform_users: [
      {
        title: 'Accounts Created',
        key: 'total_admins',
        icon: <UserAddOutlined />,
      },
      {
        title: 'Coaches Added',
        key: 'total_coaches',
        icon: <CrownOutlined />,
      },
      {
        title: 'Volunteers Added',
        key: 'total_volunteers',
        icon: <StarFilled />,
      },
    ],
  };

  const renderMetricCards = (
    category,
    typeField = 'by_event_type',
    typeKey = null
  ) => {
    let data;
    const section = statsData?.[category];
    if (!section) {
      data = {};
    } else if (!typeKey) {
      data = section;
    } else if (typeKey === 'all') {
      data = section.all;
    } else {
      data = section?.[typeField]?.[typeKey] || {};
    }

    const fields = statsMapping[category] || [];
    return (
      <div className='event_admin-dashboard-metrics__cards'>
        {fields.map(
          ({ title, key, icon, invertColours, valueMap = (v) => v }) => (
            <StatsCard
              key={`${category}${typeField}${typeKey}${key}`}
              title={title}
              value={
                data?.[key]?.value !== undefined && data?.[key]?.value !== null
                  ? valueMap(data?.[key]?.value)
                  : null
              }
              trend={data?.[key]?.trend}
              icon={icon}
              invertColours={invertColours}
              loading={loading}
              size={'medium'}
            />
          )
        )}
      </div>
    );
  };

  return (
    <Tabs
      activeKey={activeOuterKey}
      onChange={(v) => {
        setActiveOuterKey(v);
        setActiveInnerKey('all');
      }}
      items={[
        ...nestedCategories.map((cat) => ({
          key: cat,
          label: <span className='cap gSemiBold fs-18'>{cat}</span>,
          children: (
            <Tabs
              activeKey={activeInnerKey}
              onChange={setActiveInnerKey}
              items={[
                {
                  key: 'all',
                  label: <span className='cap gMedium fs-16'>All Events</span>,
                  children: renderMetricCards(
                    activeOuterKey,
                    'by_event_type',
                    'all'
                  ),
                },
                ...Object.keys(statsData?.bookings?.by_event_type || {}).map(
                  (id, i) =>
                    !eventTypes || eventTypes.find((e) => e.id === id)
                      ? {
                          key: id ?? i,
                          label: (
                            <span className='cap gMedium fs-16'>
                              {eventTypes
                                ? eventTypes.find((e) => e.id === id)?.name
                                : 'Event Type ' + i}
                            </span>
                          ),
                          children: renderMetricCards(
                            activeOuterKey,
                            'by_event_type',
                            id
                          ),
                        }
                      : null
                ),
              ]}
            />
          ),
        })),
        {
          key: 'awards',
          label: <span className='cap gSemiBold fs-18'>Awards</span>,
          children: (
            <Tabs
              activeKey={activeInnerKey}
              onChange={setActiveInnerKey}
              items={[
                {
                  key: 'all',
                  label: <span className='cap gMedium fs-16'>All Awards</span>,
                  children: renderMetricCards('awards', 'by_award', 'all'),
                },
                ...Object.keys(statsData?.awards?.by_award || {}).map(
                  (id, i) => ({
                    key: id ?? i,
                    label: (
                      <span className='cap gMedium fs-16'>
                        {awardTypes
                          ? awardTypes.find((e) => e.id === id)?.name
                          : 'Award ' + i}
                      </span>
                    ),
                    children: renderMetricCards('awards', 'by_award', id),
                  })
                ),
              ]}
            />
          ),
        },
        ...(isSuperAdmin
          ? [
              {
                key: 'users',
                label: <span className='cap gSemiBold fs-18'>Users</span>,
                children: renderMetricCards('platform_users', null, null),
              },
              {
                key: 'advert',
                label: (
                  <span className='cap gSemiBold fs-18'>Advertisement</span>
                ),
                children: (
                  <div>
                    <h3 className='gSemiBold fs-24'>
                      'How Did You Hear?' Responses
                    </h3>
                    <div className='event_admin-dashboard-metrics__cards'>
                      {[
                        'swimbooker™ App',
                        'Angling Trust Website',
                        'Social Media',
                        'Google',
                        'Word Of Mouth',
                        'Other',
                      ].map((source) => (
                        <StatsCard
                          title={source}
                          value={
                            statsData?.advertisement?.how_did_you_hear?.[source]
                              ?.value ?? 0
                          }
                          trend={
                            statsData?.advertisement?.how_did_you_hear?.[source]
                              ?.trend
                          }
                          loading={loading}
                          size={'medium'}
                        />
                      ))}
                    </div>
                  </div>
                ),
              },
            ]
          : []),
      ]}
    />
  );
}

export default StatisticsTabs;
